import { jsPDF } from "jspdf";
import logoSrc from "../assets/mfg-logo-dark.png";
import ProfileInfoController from "../controller/profileInfoController";
import TransactionHistoryController from "../controller/Transaction/transactionHistoryController";
import { sendStatementOfAccountEmail } from "./apiClient";
import AppConstants from "./appConstants";
import { TransactionType } from "./enums";

const pad2 = (n) => String(n).padStart(2, "0");

export function formatStatementDate(value) {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${pad2(value.getDate())}/${pad2(value.getMonth() + 1)}/${value.getFullYear()}`;
  }
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function getTypeCode(item) {
  return Number(item?.raw?.TYPE ?? item?.raw?.type ?? item?.typeLabel);
}

function formatMoneyAmount(n) {
  return Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMoneyCell(n, { signed = false } = {}) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "-";
  const formatted = formatMoneyAmount(Math.abs(num));
  if (signed && num < 0) return `-${formatted}$`;
  return `${formatted}$`;
}

function isCashInType(typeCode) {
  return (
    typeCode === TransactionType.CASHIN ||
    typeCode === TransactionType.CASHIN_MT5 ||
    typeCode === TransactionType.DEPOSIT
  );
}

function isCashOutType(typeCode) {
  return typeCode === TransactionType.CASHOUT || typeCode === TransactionType.WITHDRAWAL;
}

function collectUserWalletIds(userLike = {}) {
  const ids = new Set();
  const accounts = userLike?.account || userLike?.accounts || [];
  (Array.isArray(accounts) ? accounts : []).forEach((account) => {
    const wallets = account?.wallet || account?.wallets || [];
    (Array.isArray(wallets) ? wallets : []).forEach((wallet) => {
      const id = wallet?.ID ?? wallet?.id;
      if (id != null && String(id).trim() !== "") ids.add(String(id));
    });
  });
  return ids;
}

function walletIdOf(walletLike, fallbackId) {
  const id = walletLike?.ID ?? walletLike?.id ?? fallbackId;
  return id == null ? "" : String(id);
}

function parseLedgerBalances(raw = {}) {
  let ledger = raw.LEDGER_BALANCES ?? raw.ledger_balances ?? raw.ledgerBalances;
  if (!ledger) return [];
  if (typeof ledger === "string") {
    try {
      ledger = JSON.parse(ledger);
    } catch (_) {
      return [];
    }
  }
  return Array.isArray(ledger) ? ledger : [];
}

function getUpdatedBalance(item, userWalletIds) {
  const raw = item?.raw || {};
  const ledger = parseLedgerBalances(raw);
  if (ledger.length) {
    const preferred =
      ledger.find((entry) => {
        const walletId = String(entry.WALLET_ID ?? entry.wallet_id ?? "");
        return walletId && userWalletIds.has(walletId);
      }) ||
      ledger.find((entry) => {
        const symbol = String(entry.CURRENCY_SYMBOL ?? entry.currency_symbol ?? "").toUpperCase();
        return symbol === "$" || symbol === "USD";
      }) ||
      ledger[0];
    const n = Number(preferred?.BALANCE_AFTER ?? preferred?.balance_after);
    if (Number.isFinite(n)) return n;
  }

  const direct = Number(
    item?.balanceAfter ?? raw.BALANCE_AFTER ?? raw.BALANCE ?? raw.CURRENT_BALANCE ?? raw.balance
  );
  if (Number.isFinite(direct)) return direct;

  const snapshot = raw.BALANCE_SNAPSHOT ?? raw.balanceSnapshot;
  if (snapshot && typeof snapshot === "object") {
    for (const walletId of userWalletIds) {
      const n = Number(snapshot[walletId] ?? snapshot[Number(walletId)]);
      if (Number.isFinite(n)) return n;
    }
    const first = Object.values(snapshot).map(Number).find((n) => Number.isFinite(n));
    if (Number.isFinite(first)) return first;
  }
  return null;
}

function getSignedMovement(item, userWalletIds) {
  const typeCode = getTypeCode(item);
  const amount = Math.abs(Number(item?.amount));
  if (!Number.isFinite(amount) || amount === 0) return 0;
  if (isCashInType(typeCode)) return amount;
  if (isCashOutType(typeCode)) return -amount;

  const raw = item?.raw || {};
  const senderId = walletIdOf(raw.sender_wallet || raw.SENDER_WALLET, raw.SENDER_WALLET_ID);
  const receiverId = walletIdOf(
    raw.receiver_wallet || raw.RECEIVER_WALLET,
    raw.RECEIVER_WALLET_ID
  );
  const userIsReceiver = receiverId && userWalletIds.has(receiverId);
  const userIsSender = senderId && userWalletIds.has(senderId);
  if (userIsReceiver && !userIsSender) return amount;
  if (userIsSender && !userIsReceiver) return -amount;
  return 0;
}

function toStatementRows(items, userWalletIds) {
  const sorted = [...(items || [])].sort((a, b) => {
    const da = Date.parse(a?.date) || 0;
    const db = Date.parse(b?.date) || 0;
    if (da !== db) return da - db;
    return String(a?.id || "").localeCompare(String(b?.id || ""));
  });

  let runningBalance = 0;
  const rows = sorted.map((item) => {
    const typeCode = getTypeCode(item);
    const amount = Math.abs(Number(item?.amount));
    const hasAmount = Number.isFinite(amount) && amount > 0;
    const movement = getSignedMovement(item, userWalletIds);
    const apiBalance = getUpdatedBalance(item, userWalletIds);
    runningBalance = apiBalance != null ? apiBalance : runningBalance + movement;

    return {
      id: String(item?.id ?? item?.raw?.TRANSACTION_ID ?? "-"),
      date: formatStatementDate(item?.date) || "-",
      credit: isCashInType(typeCode) && hasAmount ? formatMoneyCell(amount) : "-",
      debit: isCashOutType(typeCode) && hasAmount ? formatMoneyCell(amount) : "-",
      balance: formatMoneyCell(runningBalance, { signed: true }),
    };
  });
  return { rows, endingBalance: runningBalance };
}

function sumAmountsByType(items, matcher) {
  return (items || []).reduce((sum, item) => {
    if (!matcher(getTypeCode(item))) return sum;
    const n = Number(item?.amount);
    return sum + (Number.isFinite(n) ? Math.abs(n) : 0);
  }, 0);
}

async function fetchStatementTransactions({ dateFrom, dateTo, user } = {}) {
  const result = await TransactionHistoryController.getStatementHistory({
    dateFrom,
    dateTo,
  });
  const items = result.items || [];
  const totalCredit = Number.isFinite(result.totalCredit)
    ? result.totalCredit
    : sumAmountsByType(items, isCashInType);
  const totalDebit = Number.isFinite(result.totalDebit)
    ? result.totalDebit
    : sumAmountsByType(items, isCashOutType);
  const { rows, endingBalance } = toStatementRows(items, collectUserWalletIds(user));
  return {
    rows,
    totalCredit,
    totalDebit,
    netBalance: rows.length ? endingBalance : totalCredit - totalDebit,
  };
}

function drawTransactionsTable(doc, startY, rows) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 16;
  const bottom = pageH - 14;
  const tableW = pageW - margin * 2;
  const cols = [
    { key: "date", title: "Transaction Date", w: tableW * 0.2, align: "left" },
    { key: "id", title: "Transaction Number", w: tableW * 0.28, align: "left" },
    { key: "credit", title: "Credit", w: tableW * 0.17, align: "center" },
    { key: "debit", title: "Debit", w: tableW * 0.17, align: "center" },
    { key: "balance", title: "Balance", w: tableW * 0.18, align: "right" },
  ];
  const minRowH = 8;
  const lineH = 3.6;

  const drawAlignedText = (text, x, y, w, align) => {
    if (align === "right") {
      doc.text(text, x + w - 1.6, y, { align: "right" });
      return;
    }
    if (align === "center") {
      doc.text(text, x + w / 2, y, { align: "center" });
      return;
    }
    doc.text(text, x + 1.6, y);
  };

  const drawHeader = (y) => {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(0, 0, 0);
    let x = margin;
    cols.forEach((col) => {
      doc.rect(x, y, col.w, minRowH);
      drawAlignedText(col.title, x, y + 5.3, col.w, col.headerAlign || col.align);
      x += col.w;
    });
    return y + minRowH;
  };

  let y = startY;
  if (y + minRowH * 2 > bottom) {
    doc.addPage();
    y = 16;
  }
  y = drawHeader(y);

  const body = rows.length
    ? rows
    : [{ id: "-", date: "-", credit: "-", debit: "-", balance: "-" }];

  body.forEach((row) => {
    const texts = cols.map((col) =>
      doc.splitTextToSize(String(row[col.key] ?? "-"), col.w - 3.2)
    );
    const lines = Math.max(...texts.map((t) => t.length), 1);
    const rowH = Math.max(minRowH, 3.2 + lines * lineH);
    if (y + rowH > bottom) {
      doc.addPage();
      y = drawHeader(16);
    }
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    let x = margin;
    cols.forEach((col, i) => {
      doc.rect(x, y, col.w, rowH);
      texts[i].forEach((line, lineIndex) => {
        const textY = y + 4.4 + lineIndex * lineH;
        const cellAlign =
          String(line).trim() === "-" && (col.key === "credit" || col.key === "debit")
            ? "center"
            : col.align;
        drawAlignedText(line, x, textY, col.w, cellAlign);
      });
      x += col.w;
    });
    y += rowH;
  });

  return y;
}

function drawTotalsBlock(doc, startY, { totalCredit = 0, totalDebit = 0, netBalance = 0 } = {}) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 16;
  const bottom = pageH - 14;
  const blockW = 82;
  const labelW = 38;
  const valueW = 40;
  const rowH = 7;
  const gap = 1.2;
  const blockX = pageW - margin - blockW;
  const valueX = blockX + labelW + 2;
  const totals = [
    ["Total Credit", formatMoneyCell(totalCredit)],
    ["Total Debit", formatMoneyCell(totalDebit)],
    ["Net Balance", formatMoneyCell(netBalance, { signed: true })],
  ];
  const needed = 10 + totals.length * (rowH + gap) + 4;

  let y = startY + 8;
  if (y + needed > bottom) {
    doc.addPage();
    y = 16;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.25);

  totals.forEach(([label, value]) => {
    doc.text(label, blockX, y + 4.8);
    doc.rect(valueX, y, valueW, rowH);
    doc.text(value, valueX + valueW - 1.6, y + 4.8, { align: "right" });
    y += rowH + gap;
  });

  y += 1.2;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.line(blockX, y, blockX + blockW, y);

  return y;
}

function getUserPhone(userLike = {}) {
  const info = userLike?.user_information || userLike?.userInformation || {};
  return String(
    info.PHONE ||
      info.phone ||
      info.PHONE_NUMBER ||
      info.phoneNumber ||
      info.MOBILE_NUMBER ||
      info.mobileNumber ||
      userLike?.PHONE ||
      userLike?.phone ||
      ""
  ).trim();
}

function getUserCustomerId(userLike = {}) {
  return String(
    userLike?.CLIENT_ID ??
      userLike?.clientId ??
      userLike?.client_id ??
      ""
  ).trim();
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function prepareLogo(img) {
  const source = document.createElement("canvas");
  source.width = img.naturalWidth || img.width;
  source.height = img.naturalHeight || img.height;
  const srcCtx = source.getContext("2d");
  srcCtx.drawImage(img, 0, 0);

  const { data, width, height } = srcCtx.getImageData(0, 0, source.width, source.height);
  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      const alpha = data[i + 3];
      const isNearBlack = data[i] < 22 && data[i + 1] < 22 && data[i + 2] < 22;
      if (alpha > 24 && !isNearBlack) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX <= minX || maxY <= minY) {
    return {
      dataUrl: source.toDataURL("image/png"),
      width,
      height,
    };
  }

  const pad = Math.round(Math.max(width, height) * 0.04);
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad);
  maxY = Math.min(height - 1, maxY + pad);
  const cropW = maxX - minX + 1;
  const cropH = maxY - minY + 1;

  const cropped = document.createElement("canvas");
  cropped.width = cropW;
  cropped.height = cropH;
  cropped.getContext("2d").drawImage(source, minX, minY, cropW, cropH, 0, 0, cropW, cropH);

  return {
    dataUrl: cropped.toDataURL("image/png"),
    width: cropW,
    height: cropH,
  };
}

async function resolveStatementUser(user) {
  let raw = user || {};
  let fullName = ProfileInfoController.getDisplayName(raw);
  let phone = getUserPhone(raw);
  let customerId = getUserCustomerId(raw);

  if (!user || fullName === "User" || !phone || !customerId) {
    const res = await ProfileInfoController.getProfileInfo();
    if (res?.success && res.data) {
      raw = res.data.raw || raw;
      const fromParts = [res.data.firstName, res.data.lastName]
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .join(" ");
      fullName = fromParts || ProfileInfoController.getDisplayName(raw) || fullName;
      phone = getUserPhone(raw) || phone;
      customerId =
        getUserCustomerId(raw) || String(res.data.clientId || "").trim() || customerId;
    }
  }

  return {
    fullName: String(fullName || "-").trim() || "-",
    customerId: customerId || "-",
    phone: phone || "-",
  };
}

async function buildStatementOfAccountPdf({ dateFrom, dateTo, user } = {}) {
  const [{ fullName, customerId, phone }, statement] = await Promise.all([
    resolveStatementUser(user),
    fetchStatementTransactions({ dateFrom, dateTo, user }),
  ]);
  const fromLabel = formatStatementDate(dateFrom);
  const toLabel = formatStatementDate(dateTo);
  const logoImage = await loadImage(logoSrc);
  const logo = prepareLogo(logoImage);

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const headerH = 42;
  const maxLogoH = 28;
  const maxLogoW = 52;
  const ratio = logo.width / Math.max(logo.height, 1);
  let logoH = maxLogoH;
  let logoW = logoH * ratio;
  if (logoW > maxLogoW) {
    logoW = maxLogoW;
    logoH = logoW / ratio;
  }
  const logoX = 16;
  const logoY = (headerH - logoH) / 2;

  doc.setFillColor(0, 0, 0);
  doc.rect(0, 0, pageW, headerH, "F");
  doc.addImage(logo.dataUrl, "PNG", logoX, logoY, logoW, logoH);

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Confidential Financial Statement", pageW - 16, headerH / 2 + 1.2, {
    align: "right",
    baseline: "middle",
  });

  let y = headerH + 18;
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Statement Of Account", 16, y);

  y += 8;
  doc.setFontSize(12);
  doc.text(`From ${fromLabel} Till ${toLabel}`, 16, y);

  y += 16;
  const infoMargin = 16;
  const infoGap = 14;
  const infoColW = (pageW - infoMargin * 2 - infoGap) / 2;
  const infoLeftX = infoMargin;
  const infoRightX = infoMargin + infoColW + infoGap;
  const infoLineH = 5.5;
  const infoLabelGap = 2;

  const drawInfoField = (x, startY, label, value) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    const labelText = `${label}:`;
    doc.text(labelText, x, startY);
    const valueX = x + doc.getTextWidth(labelText) + infoLabelGap;
    doc.setFont("helvetica", "normal");
    const valueLines = doc.splitTextToSize(
      String(value || "-"),
      Math.max(20, x + infoColW - valueX)
    );
    valueLines.forEach((line, i) => {
      doc.text(line, valueX, startY + i * infoLineH);
    });
    return startY + Math.max(1, valueLines.length) * infoLineH;
  };

  const row1Y = y;
  const row1Bottom = Math.max(
    drawInfoField(infoLeftX, row1Y, "Full Name", fullName.toUpperCase()),
    drawInfoField(infoRightX, row1Y, "Customer ID", customerId)
  );
  y = row1Bottom + 4;
  y =
    Math.max(
      drawInfoField(infoLeftX, y, "Phone Number", phone),
      drawInfoField(infoRightX, y, "Currency", "USD")
    ) + 4;

  y += 10;
  const tableEndY = drawTransactionsTable(doc, y, statement.rows);
  drawTotalsBlock(doc, tableEndY, statement);

  const safeFrom = fromLabel.replace(/\//g, "-") || "from";
  const safeTo = toLabel.replace(/\//g, "-") || "to";
  const filename = `statement-of-account-${safeFrom}-to-${safeTo}.pdf`;

  return {
    blob: doc.output("blob"),
    filename,
    fullName,
    fromLabel,
    toLabel,
  };
}

function triggerPdfDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export async function downloadStatementOfAccountPdf({ dateFrom, dateTo, user } = {}) {
  const { blob, filename } = await buildStatementOfAccountPdf({ dateFrom, dateTo, user });
  triggerPdfDownload(blob, filename);
}

export async function emailStatementOfAccountPdf({ dateFrom, dateTo, user } = {}) {
  const { blob, filename, fullName } = await buildStatementOfAccountPdf({
    dateFrom,
    dateTo,
    user,
  });
  const file = new File([blob], filename, { type: "application/pdf" });
  const res = await sendStatementOfAccountEmail({
    file,
    filename,
    fullName,
    dateFrom,
    dateTo,
  });
  const data = res?.decryptedData || res?.data || {};
  const code = Number(data.CODE ?? data.code ?? 0);
  if (code !== AppConstants.responseOk && code !== 200) {
    const err = new Error(data.MESSAGE || data.message || "Could not send the statement email.");
    err.code = code;
    throw err;
  }
  return data;
}
